import "./CasesTable.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { isBoolean } from "lodash";
import { DateTime } from "luxon";
import { Fragment, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  useTable,
  useExpanded,
  useSortBy,
  useFilters,
  usePagination,
  useRowSelect,
  Cell,
  CellProps,
} from "react-table";
import ReactTooltip from "react-tooltip";
import { useLocalStorage } from "react-use";

import {
  AdminCaseList,
  CaseAdmin,
  CaseQAReview,
  CaseReportSignature,
} from "pwapi";

import { requestAdminCase } from "shared/actions/admin/adminCaseActions";
import { setAdminLastCaseViewed } from "shared/actions/admin/adminCasesActions";
import { AdminCaseListDetail } from "shared/reducers/admin/adminCasesReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import PatientSexAge from "components/PatientSexAge";
import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";
import { useAppDispatch, useAppSelector } from "store/hooks";

import CaseAdminFilters from "./CaseAdminFilters";
import CasesBulkActions from "./CasesBulkActions";
import AdminPagination from "./Pagination";
import { getMostRecentQAReview, getTimeDifference } from "../adminUtils";
import EditSlidePanel from "../editSlidePanel/EditSlidePanel";
import QAReviewInformation from "../QAReviewInformation";
import CaseSlidesTable from "../SlidesTable/CaseSlidesTable";
import { getSlideStatusChange } from "../SlidesTable/SlidesColumns";
import ReactTableExpandableCell from "../table/ReactTableExpandableCell";
import ReactTableRow from "../table/ReactTableRow";
import SubTable from "../table/SubTable";
import Table from "../table/Table";
import { AdminPageSizeStorage } from "../types/adminTypes";

export const getRecentStatusLog = (changes: AdminCaseList["status_log"]) => {
  return changes.length > 1
    ? changes.reduce((dateA, dateB) =>
        new Date(dateA.created_at || "") > new Date(dateB.created_at || "")
          ? dateA
          : dateB
      )
    : changes[0];
};

const CasesTable = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  const cases = useSelector(({ admin }: ApplicationState) => admin.cases);
  const features = useSelector(({ sys }: ApplicationState) => sys.features);

  const params = useParams();
  const slide_id = parseInt(params?.slide_id || "0");

  const usp = new URLSearchParams(location.search);

  const initialParams = useMemo(() => {
    const sortBy = usp.get("sortBy");
    return {
      pageIndex: parseInt(usp.get("page") || "1") - 1,
      sortBy: sortBy
        ? sortBy.split(",").map((s) => ({
            id: s.includes("specimens__received_date")
              ? "received_date"
              : s[0] === "-"
              ? s.slice(1)
              : s,
            desc: s[0] === "-",
          }))
        : [
            {
              id: "priority",
              desc: true,
            },
          ],
    };
  }, [location]);

  const setLastViewed = (case_id: number) => {
    dispatch(setAdminLastCaseViewed(case_id));
  };

  const columns = useMemo(
    () =>
      [
        ...(features?.bulkFinalizeSignature
          ? [
              {
                id: "selection",
                Header: ({ getToggleAllRowsSelectedProps }: any) => {
                  return (
                    <input
                      type="checkbox"
                      {...getToggleAllRowsSelectedProps()}
                      indeterminate={undefined}
                      className="AdminCaselist__selector"
                      data-testid="selectAll"
                    />
                  );
                },
                Cell: ({ row }: Cell<AdminCaseList | AdminCaseListDetail>) => {
                  // eslint-disable-next-line unused-imports/no-unused-vars
                  const { indeterminate, ...rest } =
                    row.getToggleRowSelectedProps();
                  return (
                    <input
                      type="checkbox"
                      {...rest}
                      className="AdminCaselist__selector"
                    />
                  );
                },
              },
            ]
          : []),
        ...(features?.bulkFinalizeSignature && cases.bulkAssignColumns
          ? []
          : [
              {
                id: "expander",
                subComponent: "slidesTable",
                Cell: ({
                  row,
                }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
                  return row.original.slides.length ? (
                    <div className="AdminCaselist__expand">
                      <ReactTableExpandableCell row={row} />
                    </div>
                  ) : null;
                },
              },
            ]),

        {
          id: "reviewed",
          disableFilters: true,
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            const recentQAReview = getMostRecentQAReview(
              row.original.qa_reviews
            );
            const noteCount =
              row.original.qa_reviews?.filter(
                (review: CaseQAReview) => review.note
              ).length || 0;
            return (
              <>
                <div
                  style={{ cursor: "pointer" }}
                  data-for={`qaReview__${recentQAReview?.id}`}
                  data-tip=""
                >
                  {row.original.qa_reviews?.length &&
                  recentQAReview &&
                  Object.keys(recentQAReview).length > 0 ? (
                    recentQAReview?.reviewed ? (
                      <span data-testid="qaReview-check">
                        <FontAwesomeIcon
                          icon="circle-check"
                          color="var(--green)"
                        />
                      </span>
                    ) : (
                      <span data-testid="qaReview-x">
                        <FontAwesomeIcon
                          icon="circle-xmark"
                          color="var(--imperialRed)"
                        />
                      </span>
                    )
                  ) : undefined}
                </div>
                {!recentQAReview?.reviewed &&
                  recentQAReview?.user &&
                  recentQAReview?.id && (
                    <ReactTooltip
                      id={`qaReview__${recentQAReview.id}`}
                      place="top"
                      effect="solid"
                      backgroundColor="transparent"
                      borderColor="transparent"
                      textColor="black"
                      padding="0"
                    >
                      <div className="Tooltip">
                        <QAReviewInformation qaReview={recentQAReview} />
                        {!recentQAReview.reviewed &&
                          recentQAReview.note &&
                          noteCount === 1 && (
                            <span className="Tooltip__note">
                              Note: {recentQAReview.note}
                            </span>
                          )}
                      </div>
                    </ReactTooltip>
                  )}
              </>
            );
          },
        },

        {
          Header: "Priority",
          accessor: "priority",
          disableFilters: true,
          disableSortRemove: true,
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            const { priority } = row.original;
            return priority && priority !== CaseAdmin.priority.NORMAL ? (
              <div
                className={`AdminCaselist__priority AdminCaselist__priority--${priority}`}
              >
                {priority}
              </div>
            ) : row.original.patient?.clinic?.tat_priority ? (
              <div
                className={`AdminCaselist__priority AdminCaselist__priority--tat`}
              >
                Priority
              </div>
            ) : null;
          },
        },
        ...(features?.bulkFinalizeSignature && cases.bulkAssignColumns
          ? []
          : [
              {
                accessor: "overdue",
                disableFilters: true,
                Header: "Overdue",
                Cell: ({
                  row,
                }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
                  if (
                    row.original.status_log &&
                    row.original.status_log.length > 0
                  ) {
                    const currentStatusLog = getRecentStatusLog(
                      row.original.status_log
                    );
                    if (currentStatusLog.expected_next_status_by) {
                      const expectedDate = new Date(
                        currentStatusLog.expected_next_status_by
                      );
                      if (expectedDate < new Date()) {
                        return (
                          <span className="AdminCaselist__overdue">
                            {getTimeDifference(expectedDate, true)}
                          </span>
                        );
                      }
                    }
                  }
                  return <></>;
                },
              },
            ]),
        {
          Header: "Accession",
          accessor: "accession",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            return (
              <div>
                {row.original.accession}
                <br />
                {isBoolean(row.original.is_manually_accessioned) && (
                  <small data-testid={`${row.original.accession}-manualLabel`}>
                    {row.original.is_manually_accessioned
                      ? "Manual"
                      : "Ordered"}
                  </small>
                )}
              </div>
            );
          },
        },
        {
          Header: "Patient",
          accessor: "patient",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            const { patient } = row.original;
            const primaryInsurance = patient?.insurance?.find(
              (insur) => insur.primary_provider
            );
            const additionalInsurances = patient?.insurance?.filter(
              (insur) => !insur.primary_provider
            );
            return (
              patient && (
                <>
                  <span>
                    {patient.last_name}, {patient.first_name}
                  </span>
                  <br />
                  <PatientSexAge patient={patient} />
                  <br />
                  {!!primaryInsurance && (
                    <small
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        columnGap: "0.5em",
                      }}
                    >
                      <span>Primary:</span>
                      <span>
                        {primaryInsurance.insurance_company_name}
                        {primaryInsurance.policy_number && (
                          <> ({primaryInsurance.policy_number})</>
                        )}
                      </span>
                    </small>
                  )}
                  {!!additionalInsurances?.length && (
                    <small
                      style={{
                        display: "grid",
                        gridTemplateColumns: "auto 1fr",
                        columnGap: "0.5em",
                      }}
                    >
                      <span>Additional:</span>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        {additionalInsurances
                          .filter((insur) => !insur.primary_provider)
                          .map((insur) => (
                            <span key={insur.id}>
                              {insur.insurance_company_name}
                            </span>
                          ))}
                      </div>
                    </small>
                  )}
                </>
              )
            );
          },
        },
        {
          Header: "Clinic",
          accessor: "patient__clinic__name",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            return row.original.patient?.clinic ? (
              <>{row.original.patient.clinic.name}</>
            ) : (
              <></>
            );
          },
        },
        ...(cases.bulkAssignColumns && features?.bulkFinalizeSignature
          ? [
              {
                Header: "TC CLIA Certificate",
                accessor: "grossed_by_cert",
                Cell: ({
                  row,
                }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
                  return row.original.grossed_by_cert ? (
                    <>
                      <span>
                        {row.original.grossed_by_cert.name} -{" "}
                        {row.original.grossed_by_cert.number}
                        {row.original.grossed_by_cert.description && (
                          <>
                            <br />
                            {row.original.grossed_by_cert.description}
                          </>
                        )}
                      </span>
                    </>
                  ) : (
                    <></>
                  );
                },
              },
            ]
          : []),
        {
          Header: "Ordering Provider",
          accessor: "ordering_doctor",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            const { ordering_doctor } = row.original;
            return (
              ordering_doctor && (
                <>
                  <span>
                    {ordering_doctor.last_name}, {ordering_doctor.first_name}
                  </span>
                  {row.original.patient?.clinic?.name && (
                    <>
                      <br />
                      <small title="Clinic">
                        {row.original.patient?.clinic?.name || ""}
                      </small>
                    </>
                  )}
                </>
              )
            );
          },
        },
        {
          Header: "Active Assignment",
          accessor: "active_assignment",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            const { active_assignment, finalizing_signature } = row.original;
            const signature = finalizing_signature as CaseReportSignature;
            return signature ? (
              <>
                <span>
                  {signature.user?.first_name} {signature.user?.last_name},{" "}
                  {signature.user?.credentials}
                </span>
                <br />
                <small>
                  {signature?.clia_certificate && (
                    <>
                      {signature.clia_certificate.name} -{" "}
                      {signature.clia_certificate.number}
                      {signature.clia_certificate.description ? (
                        <>
                          <br />
                          {signature.clia_certificate.description}
                        </>
                      ) : (
                        <></>
                      )}
                      {signature.clia_certificate.location && (
                        <>
                          <br />
                          {signature.clia_certificate.location.street},{" "}
                          {signature.clia_certificate.location.city},{" "}
                          {signature.clia_certificate.location.state}
                        </>
                      )}
                    </>
                  )}
                </small>
              </>
            ) : (
              active_assignment && (
                <>
                  <span>
                    {active_assignment.last_name},{" "}
                    {active_assignment.first_name}
                  </span>
                </>
              )
            );
          },
        },
        {
          Header: "Date Received",
          accessor: "received_date",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            if (!row.original.received_date) {
              return null;
            }
            const receivedDateTime =
              row.original.received_date &&
              DateTime.fromISO(row.original.received_date);
            const diff =
              receivedDateTime &&
              DateTime.now().diff(receivedDateTime, ["days", "hours"]);

            if (!diff) return null;

            if (diff.days || diff.hours) {
              return (
                <>
                  {(diff?.hours > 0 || diff?.days > 0) && (
                    <>
                      <strong>
                        {diff.days > 0 ? (
                          `${diff.days} Day${
                            diff.days > 1 || diff.days < 1 ? "s" : ""
                          } Ago`
                        ) : diff.hours > 0 ? (
                          `${Math.floor(diff.hours)} Hour${
                            diff.hours > 2 || diff.hours < 1 ? "s" : ""
                          } Ago`
                        ) : (
                          <></>
                        )}
                      </strong>
                    </>
                  )}
                  <br />
                  {receivedDateTime?.toLocaleString(
                    DateTime.DATETIME_MED_WITH_WEEKDAY
                  )}
                </>
              );
            } else {
              return <></>;
            }
          },
        },
        ...(features?.bulkFinalizeSignature && cases.bulkAssignColumns
          ? []
          : [
              {
                Header: "Slides",
                Cell: ({
                  row,
                }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
                  return (
                    <ReactTableExpandableCell
                      row={row}
                      canExpand={!!row.original.slides.length}
                    >
                      <span
                        className={
                          !row.original.slides.length ? "cell__error" : ""
                        }
                      >
                        {(row.original as AdminCaseListDetail).slides
                          ? `${
                              (
                                row.original as AdminCaseListDetail
                              ).slides.filter((s) => s.status === "success")
                                .length
                            }/${row.original.slides.length}`
                          : ""}
                        {!row.original.slides.length && (
                          <span>
                            <FontAwesomeIcon icon="exclamation" />
                          </span>
                        )}
                      </span>
                    </ReactTableExpandableCell>
                  );
                },
              },
            ]),
        {
          Header: "Status",
          accessor: "status",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => (
            <>
              {row.original.status}
              {row.original.is_screening && (
                <>
                  <br />
                  <small>screening</small>
                </>
              )}
            </>
          ),
        },
        ...(features?.bulkFinalizeSignature && cases.bulkAssignColumns
          ? []
          : [
              {
                Header: "Status Age",
                accessor: "status_age",
                Cell: ({
                  row,
                }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
                  const slidesStatus = usp.get("slidesStatus");

                  let statusChanges = row.original.status_log || [];

                  if (slidesStatus) {
                    let slideStatusChanges = row.original.slides
                      .map((slide) => {
                        return getSlideStatusChange(slide);
                      })
                      // Filter for status
                      .filter((s) => s?.new_status === slidesStatus)
                      // Filter out unknowns
                      .filter((s: unknown) => s);
                    slideStatusChanges.sort((a, b) => {
                      return (
                        (a?.created_at
                          ? Date.parse(a.created_at)
                          : Date.now()) -
                        (b?.created_at ? Date.parse(b?.created_at) : Date.now())
                      );
                    });
                    if (
                      !!slideStatusChanges.length &&
                      slideStatusChanges.at(0)?.created_at
                    ) {
                      return getTimeDifference(
                        new Date(slideStatusChanges.at(0)?.created_at || "")
                      );
                    }
                  }

                  if (
                    row.original.status !== "archived" &&
                    statusChanges.length > 0
                  ) {
                    return getTimeDifference(
                      new Date(
                        getRecentStatusLog(statusChanges).created_at || ""
                      )
                    );
                  }
                  return <></>;
                },
              },
            ]),
        {
          Header: "Actions",
          Cell: ({ row }: CellProps<AdminCaseList | AdminCaseListDetail>) => {
            const case_id = row.original.id || 0;

            return (
              <div
                className="cell__content--horizontal"
                style={{ gap: "0.5em" }}
              >
                <Link
                  to={`/admin/cases/${row.original.id}`}
                  state={{
                    label: "Cases",
                    prevUrl: `${location.pathname}${location.search}`,
                  }}
                  onClick={() => {
                    setLastViewed(case_id);
                  }}
                >
                  <PathologyWatchButton title="Edit Case" size="small">
                    Edit
                  </PathologyWatchButton>
                </Link>

                <Link
                  to={`/cases/${row.original.id}`}
                  target="_blank"
                  onClick={() => {
                    setLastViewed(case_id);
                  }}
                >
                  <PathologyWatchButton title="View Case" size="small">
                    <>View&nbsp;&rarr;</>
                  </PathologyWatchButton>
                </Link>
              </div>
            );
          },
        },
      ] as any,
    [location, cases.bulkAssignColumns]
  );

  const [storagePageSize, setStoragePageSize] =
    useLocalStorage<AdminPageSizeStorage>("ReducedAdminPageSize");

  const setPageSizeToStorage = (value: string) => {
    setStoragePageSize({
      ...storagePageSize,
      cases: parseInt(value),
    });
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsExpanded,

    // Pagination
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    expandedRows,

    // Selection
    state: { sortBy, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: cases.results,
      defaultColumn: {
        sortDescFirst: true,
      },
      manualSortBy: true,
      manualFilters: true,
      autoResetFilters: false,
      autoResetExpanded: false,
      manualPagination: true,
      pageCount: -1,

      initialState: {
        ...initialParams,
        pageSize: storagePageSize?.cases || 10,
      },
    },

    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const loading = useAppSelector((state) => state.requestStatuses.loading);

  // The latest version of react-table includes a new Table Option onExpandedChange() which could replace expandedCases
  const { expandedCases, expandedCasesStr } = useMemo(() => {
    const array = expandedRows
      .filter((r) => r.isExpanded)
      .map((r) => {
        const c = cases.results.find(
          (c) => c.id === r.original.id
        ) as AdminCaseListDetail;
        return {
          id: r.original.id,
          loaded: !!c?.detailLoaded,
          error: !!c?.detailError,
          loading:
            !!loading[requestAdminCase.getRequestKey(r.original.id || 0)],
        };
      });
    return {
      expandedCases: array,
      expandedCasesStr: JSON.stringify(array),
    };
  }, [expandedRows, cases.results, loading]);

  useEffect(() => {
    expandedCases.forEach((ec) => {
      if (ec.id && !ec.loaded && !ec.loading && !ec.error) {
        dispatch(requestAdminCase(ec.id));
      }
    });
  }, [expandedCasesStr]);

  const pageCount = cases?.count ? Math.ceil(cases.count / pageSize) : -1;

  return (
    <div className="AdminCaselist">
      {slide_id ? (
        <EditSlidePanel
          slideId={slide_id}
          returnUrl={`/admin${location.search}`}
        />
      ) : undefined}
      <CaseAdminFilters
        sortBy={sortBy}
        pageIndex={pageIndex}
        pageSize={pageSize}
        toggleAllRowsExpanded={toggleAllRowsExpanded}
        setPage={gotoPage}
      />
      <div className={classNames({ "AdminCaselist--loading": cases?.loading })}>
        {cases?.loading && (
          <div className="AdminCaselist__spinner">
            <Spinner />
          </div>
        )}
        <AdminPagination
          {...{
            nextPage,
            previousPage,
            gotoPage,
            canPreviousPage,
            canNextPage: canNextPage && !!cases.next,
            totalCount: cases.count,

            pageIndex,
            pageCount,
            pageSize,
            setPageSize,

            toggleAllRowsExpanded,
            label: "cases",
            manualReviewCount: cases.slideCount,
            setToLocalStorage: setPageSizeToStorage,
            idSuffix: "upper",
            usePageSizeSelect: false,
          }}
        >
          {features?.bulkFinalizeSignature && (
            <CasesBulkActions selectedCases={selectedFlatRows} />
          )}
        </AdminPagination>
        <Table
          getTableProps={getTableProps}
          getTableBodyProps={getTableBodyProps}
          headerGroups={headerGroups.map((headerGroup, index) => (
            <tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={column.id}
                >
                  {column.render("Header")}
                  <span>
                    {column.isSorted ? (
                      column.isSortedDesc ? (
                        <FontAwesomeIcon icon="angle-down" />
                      ) : (
                        <FontAwesomeIcon icon="angle-up" />
                      )
                    ) : (
                      ""
                    )}
                  </span>
                </th>
              ))}
            </tr>
          ))}
          rows={rows.map((row) => {
            prepareRow(row);
            return (
              <Fragment key={`case:${row.original.id}`}>
                <ReactTableRow
                  row={row}
                  canExpand={
                    !!row.original.slides?.length && !cases.bulkAssignColumns
                  }
                  lastViewed={row.original.id === cases.lastViewed}
                />

                {row.isExpanded && (
                  <SubTable>
                    <CaseSlidesTable pathCase={row.original} />
                  </SubTable>
                )}
              </Fragment>
            );
          })}
        />

        <AdminPagination
          {...{
            nextPage,
            previousPage,
            gotoPage,
            canPreviousPage,
            canNextPage: canNextPage && !!cases.next,
            totalCount: cases.count,

            pageIndex,
            pageCount,
            pageSize,
            setPageSize,

            toggleAllRowsExpanded,
            label: "cases",
            manualReviewCount: cases.slideCount,
            setToLocalStorage: setPageSizeToStorage,
          }}
        />
      </div>
    </div>
  );
};

export default CasesTable;
